import $ from 'jquery';
import Storage from 'lib/localstorage';
import _ from 'lib/utils';
import 'promise-polyfill';

const SMALL_SCREEN_DEFAULT = 600;
const SMALL_SCREEN_LANDSCAPE_DEFAULT = 800;
const SMALL_WINDOW_DEFAULT = 600;

/**
* Provides User Agent information services.
* This module should remain the definitive source of data
* regarding the host system and presentation constraints.
* @module lib/user_agent
*/
let UserAgent = {
  /**
  * Checks the environment for touch support.
  * @returns {Boolean} true for touch-enabled environment.
  */
  isTouch: function() {
    if (this._touch === undefined) {
      this._touch = ('ontouchstart' in window) || (navigator.msMaxTouchPoints > 0);
    }
    return this._touch;
  },

  /**
  * Checks the environment for small-screen display.
  * NOTE: this checks content presentation ONLY.
  * If you are looking to check if this is mobile, you're better off using
  * one UserAgent.isSmallScreen() or UserAgent.isMobile();
  * This check does not reflect device type.
  *
  * isSmallWindow() measures the browser window, isSmallScreen() measures the computer monitor/phone screen size
  *
  * @param {Number} [threshold] optional size to test against.
  * @returns {Boolean} true for small format display.
  */
  isSmallWindow: function(threshold) {
    threshold = threshold || SMALL_WINDOW_DEFAULT;
    var de = document.documentElement;
    var width = window.innerWidth || (de && de.clientWidth) || document.body.clientWidth;
    return width <= threshold;
  },

  /**
  * Checks the environment for small-screen display.
  * NOTE: this checks the actual size of the device's screen, not
  * the presented windown width.
  * This check does not reflect device type.
  *
  * isSmallWindow() measures the browser window, isSmallScreen() measures the computer monitor/phone screen size
  *
  * @param {Number} [threshold] optional size to test against.
  * @param {Number} [landscape_threshold] optional size to test against if the device is in landscape.
  * @returns {Boolean} true for small format display.
  */
  isSmallScreen: function(threshold = SMALL_SCREEN_DEFAULT, landscapeThreshold = SMALL_SCREEN_LANDSCAPE_DEFAULT) {
    var orientationDegs = 0;
    if (!isNaN(parseInt(window.orientation))) { // isNaN(null) == true and i don't even want to live on this planet anymore
      // safari mobile uses window.orientation;
      orientationDegs = window.orientation;
    } else if ( !isNaN(_.get(window, 'screen.orientation.angle', NaN)) ) {
      // chrome and other browsers
      orientationDegs = window.screen.orientation.angle;
    }

    var deviceIsLandscape = (orientationDegs == 90 || orientationDegs == -90 || orientationDegs == 270);
    var width = (deviceIsLandscape) ? screen.height: screen.width;
    var thresholdToCheck = deviceIsLandscape ? landscapeThreshold : threshold;

    return width <= thresholdToCheck;
  },

  /**
  * Checks the environment for 'mobile' display.
  * NOTE: this checks content presentation and touch capability ONLY.
  * This check does _not_ reflect device type.
  * @param {Number} [threshold] optional size to test against.
  * @returns {Boolean} true for small format and touch.
  */
  isMobile: function() {
    return this.isTouch() && this.isSmallScreen();
  },


  /**
  * Specifies if the document is in full-screen mode.
  * @returns {Boolean} true if in full screen
  */

  isFullScreen: function() {
    return (document.isFullScreen || document.mozIsFullScreen || document.webkitIsFullScreen);
  },

  /**
  * Specifies if CORS ajax requests are allowed by this device.
  * @returns {Boolean} true if device allows CORS ajax requests.
  */
  isCORSEnabled: function() {
    return !window.navigator.userAgent.match(/MSIE (6|7|8|9)/);
  },

  /**
  * Specifies if Intelligent Tracking Prevention is implemented in this browser.
  * https://webkit.org/blog/7675/intelligent-tracking-prevention
  * @returns {Boolean} true if browser implements ITP.
  */
  isITPEnabled: function() {
    var match = window.navigator.userAgent.match(/(\d+(?:\.\d+)?) Safari/i);
    return match && parseFloat(match[1]) >= 11;
  },

  /**
   * Specifies the name of the current breakpoint.
   * @return {String} name of the current breakpoint.
   */
  breakpoint: function() {
    var breakPoints = [
      { name: 'xsmall',             min: 0,    max: 400  },
      { name: 'small',              min: 401,  max: SMALL_WINDOW_DEFAULT },
      { name: 'medium_small',       min: 601,  max: 728  },
      { name: 'medium',             min: 749,  max: 880  },
      { name: 'large_medium',       min: 881,  max: 950  },
      { name: 'large',              min: 956,  max: 1080 },
      { name: 'xlarge_large',       min: 1081, max: 1200 },
      { name: 'xlarge',             min: 1201, max: 1500 },
      { name: 'xxlarge',            min: 1501, max: Infinity }
    ];

    for (var i = breakPoints.length-1; i >= 0; i--) {
      var b = breakPoints[i];
      var width = screen.width;
      if (width >= b.min && width <= b.max) {
        return b.name;
      }
    }

    return b.name;
  },

  /**
  * Performs browser detection via UA sniffing.
  * Replaces jQuery's `$.browser`.
  * @returns {Object} stats object with vendor keys and a version number.
  */
  browser: function() {
    if (!this._browser) {
      var ua = navigator.userAgent.toLowerCase();
      var match = /(chrome)[ \/]([\w.]+)/.exec(ua) ||
        /(webkit)[ \/]([\w.]+)/.exec(ua) ||
        /(opera)(?:.*version|)[ \/]([\w.]+)/.exec(ua) ||
        /(msie) ([\w.]+)/.exec(ua) ||
        /(firefox)[ \/]([\w.]+)/.exec(ua) ||
        /(trident)[ \/]([\w.]+)/.exec(ua) ||
        ua.indexOf('compatible') < 0 && /(mozilla)(?:.*? rv:([\w.]+)|)/.exec(ua) ||
        [];
      var browser = match[ 1 ] || '';
      var version = match[ 2 ] || '0';
      var stats = this._browser = {};

      if (browser) {
        stats[browser] = true;
        stats.version = version;

        if (navigator.appName === 'Netscape' && browser === 'trident') {
          // Hello, my name is Netscape and I'm a Trident browser...
          // but you can call me Internet Explorer 11.
          stats.msie = true;
          stats.version = '11';
        }
      }

      if (stats.chrome) {
        stats.webkit = true;
      } else if (stats.webkit) {
        stats.safari = true;
      }
    }
    return this._browser;
  },

  /* UA Sniffing to get at ios version, we need to make some sligh adjust ments
  * to the UI based on some funny business on early versions on IOS
  * @returns {Object} with majorVersion and minorVersion as integers.
  * @example
  * import UserAgent from 'lib/user_agent';
  *
  * UserAgent.iOSversion().majorVersion; // << check for, say, iOS8
  * UserAgent.iOSversion().minorVersion; // << check for, say, iOS 8.1
  */
  iOSversion: function() {
    if (/iP(hone|od|ad)/.test(navigator.appVersion)) {
      // supports iOS 2.0 and later: <http://bit.ly/TJjs1V>
      var v = (navigator.appVersion).match(/OS (\d+)_(\d+)_?(\d+)?/);
      return {
        iOS : true,
        majorVersion : parseInt(v[1], 10),
        minorVersion: parseInt(v[2], 10)
      };
    }
    return {
      iOS : false
    };
  },

  /**
  * Detection for image format support.
  * @returns {Promise} resolved upon all image format tests finishing.
  * The promise callback accepts an object with keys for supported formats.
  * Possible values include "webp" and "jp2" (future).
  * @example
  * import UserAgent from 'lib/user_agent';
  *
  * UserAgent.imageFormats().then(function(formats) {
  *   if (formats.webp) { ...
  *   if (formats.jp2) { ...
  * });
  */
  imageFormats: function() {
    if (!this._imgs) {
      const CACHE_VERSION = 1;
      const CACHE_NS = 'chorus-imgs-v'+ CACHE_VERSION;
      var self = this;
      var deferred = this._imgs = $.Deferred();
      var cached = Storage.get(CACHE_NS);
      var preferredOrder = ['webp']; // << add 'jp2', etc.

      if (cached && cached.hasOwnProperty(preferredOrder[0])) {
        // FULFILL FROM CACHE:
        deferred.resolve(cached);
      } else {
        // RUN STORAGE CHECKS:
        var formats = {
          'webp': 'data:image/webp;base64,UklGRiYAAABXRUJQVlA4IBoAAAAwAQCdASoBAAEAAIAOJaQAA3AA/v89WAAAAA=='
          // ,'jp2': 'data:image/jp2;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAABmJLR0QAAAAAAAD5Q7t/AAAACXBIWXMAAAsSAAALEgHS3X78AAAADUlEQVQI12P4////fwAJ+wP90YOM8AAAAABJRU5ErkJggg=='
        };

        var checks = Object.keys(formats);
        var complete = 0;

        // Done callback, invoked after each completed check:
        var done = function() {
          complete += 1;
          if (complete === checks.length) {
            // Set preferred image format based on the best available type:
            formats.preferred = preferredOrder.filter(function(type) { return formats[type]; })[0];

            // BUG FIX: issues with mobile devices responding to Thumbor headers.
            // Set no preferred image format on mobile/touch devices for now.
            // @todo remove after Thumbor headers are resolved.
            if (self.isTouch()) {
              formats.preferred = null;
            }

            Storage.set(CACHE_NS, formats);
            deferred.resolve(formats);
          }
        };

        // Run all image format checks:
        checks.forEach(function(format) {
          var img = new Image();
          img.onload = function() {
            formats[format] = (img.height > 0 && img.width > 0);
            done();
          };
          img.onerror = function() {
            formats[format] = false;
            done();
          };
          img.src = formats[format];
        });
      }
    }

    return this._imgs.promise();
  },

  /**
  * Specifies if Picture element is supported by this browser: http://caniuse.com/#search=picture
  * @returns {Boolean} true if supported.
  */
  supportsPictureElement: function() {
    var ua = navigator.userAgent;
    var webkit = /(webkit)[ \/]([\w.]+)/.exec(ua.toLowerCase());
    var chrome = /(chrome)[ \/]([\w.]+)/.exec(ua.toLowerCase()); // Chrome reports using older version of webkit but uses blink, wtf
    var edge = /(Edge)[ \/]([\w.]+)/.exec(ua);
    var ie = /MSIE|Trident/.test(ua);
    var unsupportedBrowser =  (!chrome && webkit && parseFloat(webkit[2]) < 601.6) || // Safari < 9.1 / webkit < 601.6
                              ie || // All versions of IE
                              (edge && parseFloat(edge[2]) < 13.10547) || // Edge < 13.10547
                              /Opera Mobi/.test(ua); // All versions of Opera Mobile

    return !unsupportedBrowser;
  },

  /**
   * Detects whether autoplay video is supported on this browser.
   * This is especially useful when attempting to trigger already-played video
   * on mobile devices that don't support autoplay video throws the user into
   * fullscreen mode unexpectedly.
   *
   * Inspired by https://github.com/Modernizr/Modernizr/blob/master/feature-detects/video/autoplay.js
   *
   * @return {Promise} resolves if true, rejects if false
   * @example
   * import UserAgent from 'lib/user_agent';
   *
   * UserAgent.supportsAutoplayVideo()
   *  .then(() => {
   *    // Autoplay is supported
   *  })
   *  .catch(() => {
   *    // Video src not supported
   *  });
   */
  supportsAutoplayVideo: function() {
    return new Promise((resolve, reject) => {
      let timeout;
      let currentTry = 0;
      const retries = 5;
      const waitTime = 200;

      let video = document.createElement('video');

      function testAutoplay(arg) {
        currentTry++;
        clearTimeout(timeout);

        let result = arg && arg.type === 'playing' || video.currentTime !== 0;

        if (!result && currentTry < retries) {
          // Detection can be flaky if the browser is slow, so lets retry in a little bit
          timeout = setTimeout(testAutoplay, waitTime);
          return;
        }

        // Cleanup, but don't assume elem is still in the page -
        // an extension (eg Flashblock) may already have removed it.
        if (video.parentNode) {
          video.parentNode.removeChild(video);
        }

        result ? resolve() : reject();
      }

      if (this.prefersReducedMotion()) {
        reject();
      } else if (video.canPlayType('video/ogg; codecs="theora"').replace(/^no$/, '')) {
        video.src = 'data:video/ogg;base64,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';
      } else if (video.canPlayType('video/mp4; codecs="avc1.42E01E"').replace(/^no$/, '')) {
        video.src = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZQAAAs1tZGF0AAACrgYF//+q3EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE0OCByMjYwMSBhMGNkN2QzIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAxNSAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTEgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MzoweDExMyBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MSBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTEgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0zIGJfcHlyYW1pZD0yIGJfYWRhcHQ9MSBiX2JpYXM9MCBkaXJlY3Q9MSB3ZWlnaHRiPTEgb3Blbl9nb3A9MCB3ZWlnaHRwPTIga2V5aW50PTI1MCBrZXlpbnRfbWluPTEwIHNjZW5lY3V0PTQwIGludHJhX3JlZnJlc2g9MCByY19sb29rYWhlYWQ9NDAgcmM9Y3JmIG1idHJlZT0xIGNyZj0yMy4wIHFjb21wPTAuNjAgcXBtaW49MCBxcG1heD02OSBxcHN0ZXA9NCBpcF9yYXRpbz0xLjQwIGFxPTE6MS4wMACAAAAAD2WIhAA3//728P4FNjuZQQAAAu5tb292AAAAbG12aGQAAAAAAAAAAAAAAAAAAAPoAAAAZAABAAABAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACAAACGHRyYWsAAABcdGtoZAAAAAMAAAAAAAAAAAAAAAEAAAAAAAAAZAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAEAAAAAAAgAAAAIAAAAAACRlZHRzAAAAHGVsc3QAAAAAAAAAAQAAAGQAAAAAAAEAAAAAAZBtZGlhAAAAIG1kaGQAAAAAAAAAAAAAAAAAACgAAAAEAFXEAAAAAAAtaGRscgAAAAAAAAAAdmlkZQAAAAAAAAAAAAAAAFZpZGVvSGFuZGxlcgAAAAE7bWluZgAAABR2bWhkAAAAAQAAAAAAAAAAAAAAJGRpbmYAAAAcZHJlZgAAAAAAAAABAAAADHVybCAAAAABAAAA+3N0YmwAAACXc3RzZAAAAAAAAAABAAAAh2F2YzEAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAgACAEgAAABIAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAY//8AAAAxYXZjQwFkAAr/4QAYZ2QACqzZX4iIhAAAAwAEAAADAFA8SJZYAQAGaOvjyyLAAAAAGHN0dHMAAAAAAAAAAQAAAAEAAAQAAAAAHHN0c2MAAAAAAAAAAQAAAAEAAAABAAAAAQAAABRzdHN6AAAAAAAAAsUAAAABAAAAFHN0Y28AAAAAAAAAAQAAADAAAABidWR0YQAAAFptZXRhAAAAAAAAACFoZGxyAAAAAAAAAABtZGlyYXBwbAAAAAAAAAAAAAAAAC1pbHN0AAAAJal0b28AAAAdZGF0YQAAAAEAAAAATGF2ZjU2LjQwLjEwMQ==';
      } else {
        reject();
      }

      video.style.position = 'absolute';
      video.style.height = 0;
      video.style.width = 0;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('autoplay', '');
      video.style.display = 'none';

      document.documentElement.appendChild(video);

      // wait for the next tick to add the listener, otherwise the element may
      // not have time to play in high load situations (e.g. the test suite)
      setTimeout(function() {
        video.play(); // Manual call to play sometimes required on mobile devices
        video.addEventListener('playing', testAutoplay, false);
        timeout = setTimeout(testAutoplay, waitTime);
      }, 0);
    });
  },

  /**
  * Specifies if CSS Object Fit property is supported by this browser: http://caniuse.com/#search=objectfit
  * @returns {Boolean} true if supported.
  */
  supportsObjectFit: function() {
    return ('objectFit' in document.documentElement.style);
  },

  /**
  * Checks to see if the user has signaled they’d prefer reduced animations. (https://css-tricks.com/revisiting-prefers-reduced-motion-the-reduced-motion-media-query/)
  * NOTE: This is an _optimistic_ test, and currently passes old browsers/devices.
  * (Ideally, )
  * @returns {Boolean} true if the preference is set.
  */
  prefersReducedMotion: function() {
    const motionQuery = '(prefers-reduced-motion: reduce)';

    // Return early if matchMedia’s not available
    if (typeof window.matchMedia !== 'function') {
      return false;
    }

    // Check the value of `(prefers-reduced-motion)`
    // (`prefersReduced.matches` should evaluate to `true` if a motion preference is set)
    const prefersReduced = window.matchMedia(motionQuery);

    return prefersReduced.matches;
  },

  /**
  * Specifies if IntersectionObserver api is supported by this browser: http://caniuse.com/#search=objectfit
  * @returns {Boolean} true if supported.
  */
  supportsIntersectionObserver: function() {
    return (!!window.IntersectionObserver);
  }

};

export default UserAgent;
